import React, { useEffect, useState, useRef } from "react";
import { loginRequest } from "./authConfig";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import localStorageService from "../services/localStorageService";
import useSyncSetState from "../hooks/use-sync-set-state";
import "sweetalert2/dist/sweetalert2.min.css";
import { useTranslation } from "react-i18next";

const eventTypes = [
    "keypress",
    "mousemove",
    "mousedown",
    "scroll",
    "touchmove",
    "pointermove"
];

const TIME_TO_COUNTDOWN = 60 * 1000; // 1 minute
const WARNING_DELAY = parseInt(process.env.REACT_APP_SESSION_EXPIRED_WARNING_DELAY_MS, 10) || 15 * 60 * 1000; // 15 minutes
const INTERVAL_MS = 1000; // 1 second
const ACTIVITY_DEBOUNCE_MS = 1000; // only write activity once per second

const SessionTimeout = () => {
    const { t } = useTranslation();
    const [isWarningModalOpen, setWarningModalOpen] = useSyncSetState(
        "sessionTimeoutWarningModalOpen",
        false
    );
    const [referenceTime, setReferenceTime] = useSyncSetState(
        "sessionTimeout",
        Date.now() + TIME_TO_COUNTDOWN
    );
    const [sessionExpired, setSessionExpired] = useSyncSetState(
        "sessionExpired",
        false
    );
    const [time, setTime] = useState(TIME_TO_COUNTDOWN);

    const countdownIntervalRef = useRef(null);
    const inactivityTimeoutRef = useRef(null);
    const storageHandlerRef = useRef(null);
    const lastActivityWriteRef = useRef(0);
    const hasHandledLogout = useRef(false);

    const msal = useMsal();
    const { instance } = msal;

    // Request notification permission on mount
    useEffect(() => {
        if ("Notification" in window && Notification.permission === "default") {
            Notification.requestPermission();
        }
    }, []);

    // 1. Countdown effect: when warning modal is open, run a single setInterval
    useEffect(() => {
        if (isWarningModalOpen) {
            // If permission is granted, show a browser notification
            if (
                document.visibilityState !== "visible" &&
                "Notification" in window &&
                Notification.permission === "granted"
            ) {
                const notif = new Notification(t("Session Timeout"), {
                    body: t("You're being timed out due to inactivity. Please choose to stay signed in or to sign out. Otherwise, you will be signed out automatically."),
                    silent: true,
                    icon: "/assets/images/IGL_BOX_Logo.png",
                });

                // when the user clicks the notification, focus (or open) this tab
                notif.onclick = () => {
                    // if this page is already open, focus it:
                    if (window.focus) window.focus();
                    // if you need to navigate to a specific URL, uncomment:
                    // window.location.href = "/your-path";
                }
            }

            countdownIntervalRef.current = setInterval(() => {
                setTime(() => {
                    const remaining = referenceTime + 1000 - Date.now();
                    return remaining >= 0 ? remaining : 0;
                });
            }, INTERVAL_MS);
        } else {
            setTime(TIME_TO_COUNTDOWN);
        }
        return () => {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        };
    }, [isWarningModalOpen, referenceTime, t]);

    // 2. When time reaches zero, mark sessionExpired
    useEffect(() => {
        if (time <= 0 && !sessionExpired) {
            setWarningModalOpen(false);
            setSessionExpired(true);
        }
    }, [time, sessionExpired, setWarningModalOpen, setSessionExpired]);

    // 3. When sessionExpired becomes true, trigger logout or reload (only once)
    useEffect(() => {
        if (!sessionExpired || hasHandledLogout.current) return;
        hasHandledLogout.current = true;

        localStorageService.removeItem("sessionExpired");
        localStorageService.removeItem("sessionTimeoutWarningModalOpen");
        localStorageService.removeItem("sessionTimeout");

        if (instance.getActiveAccount()) {
            setWarningModalOpen(false);
            instance.logoutRedirect().catch((e) => {
                console.error("Logout redirect error:", e);
            });
        } else {
            window.location.reload();
        }
    }, [sessionExpired, instance, setWarningModalOpen]);

    // 4. Inactivity timer + event listeners + cross-tab signaling
    useEffect(() => {
        const writeActivity = () => {
            const now = Date.now();
            if (now - lastActivityWriteRef.current < ACTIVITY_DEBOUNCE_MS) return;
            lastActivityWriteRef.current = now;
            localStorageService.setItem("lastActive", "1");
            // clear the flag shortly after so next write is a real change
            setTimeout(() => {
                localStorageService.removeItem("lastActive");
            }, ACTIVITY_DEBOUNCE_MS);
        };

        const scheduleWarning = () => {
            clearTimeout(inactivityTimeoutRef.current);
            writeActivity();
            inactivityTimeoutRef.current = setTimeout(() => {
                setReferenceTime(Date.now() + TIME_TO_COUNTDOWN);
                setWarningModalOpen(true);
            }, WARNING_DELAY);
        };

        const activityDetected = () => {
            if (!isWarningModalOpen) {
                scheduleWarning();
            }
        };

        // initial schedule
        if (!isWarningModalOpen) {
            scheduleWarning();
        }

        // add DOM event listeners
        eventTypes.forEach((type) => {
            window.addEventListener(type, activityDetected, false);
        });

        // storage listener for cross-tab signals
        const handleStorageEvent = (event) => {
            if (event.storageArea !== localStorage) return;

            let eventNewValue;
            try {
                eventNewValue =  JSON.parse(event.newValue) // this gets exact value added by localStorageService
            } catch {}

            // another tab signaled activity
            if (event.key === "lastActive" && eventNewValue === "1") {
                activityDetected();
            }

            // another tab signaled logout
            if (event.key === "signOut" && eventNewValue === "1") {
                if (!hasHandledLogout.current) {
                    hasHandledLogout.current = true;
                    if (instance.getActiveAccount()) {
                        instance.logoutRedirect().catch((e) => {
                            console.error("Logout redirect error:", e);
                        });
                    } else {
                        window.location.reload();
                    }
                }
            }
        };
        storageHandlerRef.current = handleStorageEvent;
        window.addEventListener("storage", handleStorageEvent);

        return () => {
            clearTimeout(inactivityTimeoutRef.current);
            inactivityTimeoutRef.current = null;

            eventTypes.forEach((type) => {
                window.removeEventListener(type, activityDetected, false);
            });

            if (storageHandlerRef.current) {
                window.removeEventListener("storage", storageHandlerRef.current);
                storageHandlerRef.current = null;
            }
        };
    }, [instance, isWarningModalOpen, setReferenceTime, setWarningModalOpen]);

    const acquireNewToken = (instance) => {
        const request = {
            ...loginRequest,
            account: instance.getActiveAccount(),
        };
        instance
            .acquireTokenSilent(request)
            .then((response) => {
                if (!response.idToken || response.idToken === "") {
                    console.log("No token returned, initiating interactive login.");
                }
                return response;
            })
            .catch((error) => {
                console.log(
                    "Silent token acquisition failed. Falling back to redirect.",
                    error
                );
                if (error instanceof InteractionRequiredAuthError) {
                    instance.loginRedirect(loginRequest).catch((e) => {
                        console.error("Login redirect failed:", e);
                    });
                } else {
                    console.error(error);
                }
            });
    };

    const handleStayLoggedIn = () => {
        setWarningModalOpen(false);
        localStorageService.setItem("lastActive", "1");
        setTimeout(() => {
            localStorageService.removeItem("lastActive");
        }, ACTIVITY_DEBOUNCE_MS);
        acquireNewToken(instance);
    };

    const handleSignOut = () => {
        // 1. Signal all tabs to sign out
        localStorageService.setItem("signOut", "1");
        // 2. Clear the signOut flag after a short delay so newly mounted tabs won't loop
        setTimeout(() => {
            localStorageService.removeItem("signOut");
        }, 100);

        // 3. Delay actual logoutRedirect to ensure other tabs receive the storage event first
        setTimeout(() => {
            setWarningModalOpen(false);
            instance.logoutRedirect().catch((e) => {
                console.error("Logout redirect error:", e);
            });
        }, 200);
    };

    return (
        <>
            {isWarningModalOpen && (
                <div
                    className="swal2-container swal2-center swal2-backdrop-show"
                    style={{ overflowX: "auto" }}
                >
                    <div
                        className="swal2-popup swal2-modal swal2-icon-warning swal2-show"
                        style={{ display: "grid" }}
                    >
                        <div
                            className="swal2-icon swal2-warning swal2-icon-show"
                            style={{ display: "flex" }}
                        >
                            <div className="swal2-icon-content">!</div>
                        </div>
                        <h2 className="swal2-title d-block" id="swal2-title">
                            {t("Session Timeout")}
                        </h2>
                        <div className="swal2-content">
                            <div
                                id="swal2-content"
                                className="swal2-html-container d-block"
                            >
                                {t("You're being timed out due to inactivity. Please choose to stay signed in or to sign out. Otherwise, you will be signed out automatically.")}
                            </div>
                        </div>
                        <div className="swal2-actions">
                            <button
                                type="button"
                                className="swal2-confirm swal2-styled d-inline-block"
                                style={{
                                    backgroundColor: "rgb(40, 167, 69)",
                                    borderLeftColor: "rgb(64, 83, 101)",
                                    borderRightColor: "rgb(64, 83, 101)",
                                }}
                                onClick={handleStayLoggedIn}
                            >
                                {t("Stay Logged in")} ({Math.floor(time / 1000)})
                            </button>
                            <button
                                type="button"
                                className="swal2-cancel swal2-styled"
                                style={{ backgroundColor: "rgb(221, 51, 51)" }}
                                onClick={handleSignOut}
                            >
                                {t("Sign out")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default SessionTimeout;